import React, { useEffect, useState } from "react";
import * as THREE from "three";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Camera, Ruler, Check, Undo2, X } from "lucide-react";
import { useApp } from "@/context/AppContext";

// Brand accent (METEROX yellow) — HSL(45,100%,50%)
const BRAND = 0xffbf00;
const BRAND_CSS = "#ffbf00";

// Shoelace area on the floor plane (x/z), in m²
const polygonArea = (pts) => {
  if (pts.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length];
    a += p.x * q.z - q.x * p.z;
  }
  return Math.abs(a) / 2;
};

const clearGroup = (g) => {
  while (g.children.length) {
    const c = g.children.pop();
    if (c.geometry) c.geometry.dispose();
    if (c.material) {
      if (c.material.map) c.material.map.dispose();
      c.material.dispose();
    }
    g.remove(c);
  }
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Billboard text sprite drawn on a canvas texture
const makeLabel = (text, { fontSize = 52, pad = 22, bg = "rgba(17,17,17,0.85)", fg = BRAND_CSS, scale = 0.1 } = {}) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  const tw = ctx.measureText(text).width;
  canvas.width = Math.ceil(tw + pad * 2);
  canvas.height = Math.ceil(fontSize + pad * 2);
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.fillStyle = bg;
  roundRect(ctx, 0, 0, canvas.width, canvas.height, 18);
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set((scale * canvas.width) / canvas.height, scale, 1);
  sprite.renderOrder = 999;
  return sprite;
};

// Reusable area calculator: works on every device (iPhone/Android/desktop).
// Sums rectangular sections (handles L-shapes by adding sections).
// Offers a real WebXR AR measure with 3D markers/lines/area where supported (Android/Chrome + ARCore).
export const AreaCalculator = ({ open, onClose, onConfirm }) => {
  const { t } = useApp();
  const [rooms, setRooms] = useState([{ name: "", sections: [{ w: "", l: "" }], ar: null }]);
  const [arSupported, setArSupported] = useState(false);
  const [arActive, setArActive] = useState(false);
  const [arCount, setArCount] = useState(0);
  const [liveArea, setLiveArea] = useState(0);
  const [arReady, setArReady] = useState(false);
  const overlayRef = React.useRef(null);
  const three = React.useRef(null);
  const readyRef = React.useRef(false);
  const everHitRef = React.useRef(false);
  const arState = React.useRef({ session: null, hit: null, points: [] });
  const arRoomIdx = React.useRef(0);

  useEffect(() => {
    if (!open) return;
    setRooms([{ name: `${t("roomLabel")} 1`, sections: [{ w: "", l: "" }], ar: null }]);
    if (navigator.xr && navigator.xr.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-ar").then(setArSupported).catch(() => setArSupported(false));
    }
  }, [open]);

  const num = (v) => (parseFloat(String(v).replace(",", ".")) || 0);
  const sectionsSum = (secs) => secs.reduce((s, r) => s + num(r.w) * num(r.l), 0);
  const roomArea = (room) => (room.ar != null ? room.ar : sectionsSum(room.sections));
  const total = rooms.reduce((s, room) => s + roomArea(room), 0);

  const setRoom = (ri, patch) => setRooms((arr) => arr.map((room, idx) => (idx === ri ? { ...room, ...patch } : room)));
  const renameRoom = (ri, name) => setRoom(ri, { name });
  const addRoom = () => setRooms((arr) => [...arr, { name: `${t("roomLabel")} ${arr.length + 1}`, sections: [{ w: "", l: "" }], ar: null }]);
  const removeRoom = (ri) => setRooms((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== ri) : arr));
  const clearRoomAr = (ri) => setRoom(ri, { ar: null });

  const updateSection = (ri, si, key, val) =>
    setRooms((arr) => arr.map((room, idx) => (idx === ri ? { ...room, sections: room.sections.map((s, j) => (j === si ? { ...s, [key]: val } : s)) } : room)));
  const addSection = (ri) =>
    setRooms((arr) => arr.map((room, idx) => (idx === ri ? { ...room, sections: [...room.sections, { w: "", l: "" }] } : room)));
  const removeSection = (ri, si) =>
    setRooms((arr) => arr.map((room, idx) => (idx === ri ? { ...room, sections: room.sections.length > 1 ? room.sections.filter((_, j) => j !== si) : room.sections } : room)));

  // Rebuild markers, edges, area fill and labels from current points
  const rebuild = () => {
    const T = three.current;
    if (!T) return;
    const pts = arState.current.points;
    clearGroup(T.markerGroup);
    clearGroup(T.lineGroup);
    clearGroup(T.labelGroup);
    if (T.fillMesh) {
      T.scene.remove(T.fillMesh);
      T.fillMesh.geometry.dispose();
      T.fillMesh.material.dispose();
      T.fillMesh = null;
    }

    // Persistent markers on each placed point
    pts.forEach((p) => {
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(0.028, 20, 20),
        new THREE.MeshStandardMaterial({ color: BRAND, emissive: BRAND, emissiveIntensity: 0.6, roughness: 0.35 })
      );
      m.position.set(p.x, p.y, p.z);
      T.markerGroup.add(m);
    });

    // Edges + per-side length labels
    const n = pts.length;
    const edges = n >= 3 ? n : n - 1;
    for (let i = 0; i < Math.max(0, edges); i++) {
      const a = pts[i];
      const b = pts[(i + 1) % n];
      if (!b) break;
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(a.x, a.y, a.z),
        new THREE.Vector3(b.x, b.y, b.z),
      ]);
      const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: BRAND }));
      line.renderOrder = 2;
      T.lineGroup.add(line);
      const dist = Math.hypot(b.x - a.x, b.z - a.z);
      const lbl = makeLabel(`${dist.toFixed(2)} m`, { scale: 0.085 });
      lbl.position.set((a.x + b.x) / 2, (a.y + b.y) / 2 + 0.05, (a.z + b.z) / 2);
      T.labelGroup.add(lbl);
    }

    // Filled polygon + total area label
    if (n >= 3) {
      const shape = new THREE.Shape();
      shape.moveTo(pts[0].x, -pts[0].z);
      for (let i = 1; i < n; i++) shape.lineTo(pts[i].x, -pts[i].z);
      shape.closePath();
      const geo = new THREE.ShapeGeometry(shape);
      geo.rotateX(-Math.PI / 2);
      const mat = new THREE.MeshBasicMaterial({
        color: BRAND,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      const avgY = pts.reduce((s, p) => s + p.y, 0) / n;
      mesh.position.y = avgY + 0.006;
      mesh.renderOrder = 1;
      T.scene.add(mesh);
      T.fillMesh = mesh;

      const cx = pts.reduce((s, p) => s + p.x, 0) / n;
      const cz = pts.reduce((s, p) => s + p.z, 0) / n;
      const albl = makeLabel(`${polygonArea(pts).toFixed(2)} m²`, {
        scale: 0.16,
        fg: "#111111",
        bg: "rgba(255,191,0,0.96)",
      });
      albl.position.set(cx, avgY + 0.07, cz);
      T.labelGroup.add(albl);
    }

    setArCount(n);
    setLiveArea(polygonArea(pts));
  };

  const cleanupAR = () => {
    const T = three.current;
    if (T) {
      try { T.renderer.setAnimationLoop(null); } catch { /* noop */ }
      try { T.renderer.dispose(); } catch { /* noop */ }
    }
    three.current = null;
    arState.current = { session: null, hit: null, points: [] };
    readyRef.current = false;
    setArActive(false);
    setArReady(false);
    setArCount(0);
    setLiveArea(0);
  };

  const startAR = async () => {
    try {
      // Let three.js create its own WebGL2 context (r185+ requires WebGL2)
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.xr.enabled = true;
      renderer.xr.setReferenceSpaceType("local");

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera();
      scene.add(new THREE.HemisphereLight(0xffffff, 0xbbbbbb, 1.4));
      const dir = new THREE.DirectionalLight(0xffffff, 0.9);
      dir.position.set(0.5, 1, 0.25);
      scene.add(dir);

      // Floor reticle (ring + inner dot) that follows hit test
      const reticle = new THREE.Group();
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.05, 0.075, 40).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial({ color: BRAND, transparent: true, opacity: 0.95, depthTest: false, side: THREE.DoubleSide })
      );
      const dot = new THREE.Mesh(
        new THREE.CircleGeometry(0.014, 24).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, side: THREE.DoubleSide })
      );
      ring.renderOrder = 1000;
      dot.renderOrder = 1001;
      reticle.add(ring, dot);
      reticle.matrixAutoUpdate = false;
      reticle.visible = false;
      scene.add(reticle);

      const markerGroup = new THREE.Group();
      const lineGroup = new THREE.Group();
      const labelGroup = new THREE.Group();
      scene.add(markerGroup, lineGroup, labelGroup);

      const requestAR = (optionalFeatures) =>
        navigator.xr.requestSession("immersive-ar", {
          requiredFeatures: ["hit-test", "local"],
          optionalFeatures,
          domOverlay: { root: overlayRef.current },
        });

      let session = await requestAR(["dom-overlay", "layers"]);
      try {
        await renderer.xr.setSession(session);
      } catch (err) {
        // Some ARCore/Chrome builds reject the layers path: retry with a plain session
        console.warn("XR setSession failed, retrying without layers:", err);
        try { await session.end(); } catch { /* noop */ }
        session = await requestAR(["dom-overlay"]);
        await renderer.xr.setSession(session);
      }

      const viewerSpace = await session.requestReferenceSpace("viewer");
      const hitSource = await session.requestHitTestSource({ space: viewerSpace });

      three.current = { renderer, scene, camera, reticle, markerGroup, lineGroup, labelGroup, fillMesh: null };
      arState.current = { session, hit: null, points: [] };
      readyRef.current = false;
      everHitRef.current = false;
      setArActive(true);
      setArReady(false);
      setArCount(0);
      setLiveArea(0);

      session.addEventListener("end", cleanupAR);

      const setTracking = (v) => {
        if (readyRef.current !== v) { readyRef.current = v; setArReady(v); }
        if (v) everHitRef.current = true;
      };

      renderer.setAnimationLoop((time, frame) => {
        if (frame) {
          const refSpace = renderer.xr.getReferenceSpace();
          const results = refSpace ? frame.getHitTestResults(hitSource) : [];
          const pose = results.length ? results[0].getPose(refSpace) : null;
          if (pose) {
            // Full pose matrix: follows floor depth and slope every frame
            reticle.matrix.fromArray(pose.transform.matrix);
            reticle.matrixWorldNeedsUpdate = true;
            reticle.visible = true;
            ring.material.opacity = 0.95;
            const p = pose.transform.position;
            arState.current.hit = { x: p.x, y: p.y, z: p.z };
            setTracking(true);
          } else {
            // Keep the reticle at its last floor pose (dimmed) so it never disappears
            ring.material.opacity = 0.4;
            arState.current.hit = null;
            setTracking(false);
          }
        }
        renderer.render(scene, camera);
      });
    } catch (e) {
      console.error("AR start failed:", e);
      cleanupAR();
      alert(t("arError"));
    }
  };

  const startRoomAR = (ri) => { arRoomIdx.current = ri; startAR(); };

  const arAddPoint = () => {
    if (!arState.current.hit) return;
    arState.current.points.push({ ...arState.current.hit });
    rebuild();
  };
  const arUndo = () => {
    arState.current.points.pop();
    rebuild();
  };
  const arFinish = async () => {
    const area = polygonArea(arState.current.points);
    if (area > 0) {
      const ri = arRoomIdx.current;
      // Commit the measured area to the room FIRST (before tearing down the session/overlay),
      // so the value is guaranteed and we stay on the rooms screen.
      setRooms((arr) => arr.map((room, idx) => (idx === ri ? { ...room, ar: Math.round(area * 100) / 100 } : room)));
    }
    try { await arState.current.session?.end(); } catch { /* noop */ }
  };
  const arCancel = async () => {
    try { await arState.current.session?.end(); } catch { /* noop */ }
  };

  const confirm = () => {
    const summary = rooms
      .map((r, i) => ({ name: (r.name && r.name.trim()) || `${t("roomLabel")} ${i + 1}`, area: Math.round(roomArea(r) * 100) / 100 }))
      .filter((r) => r.area > 0);
    onConfirm(Math.round(total * 100) / 100, summary);
    onClose();
  };

  return (
    <>
      {/* WebXR dom-overlay root (Android). Rendered always so the ref exists. */}
      <div ref={overlayRef} style={{ display: arActive ? "block" : "none" }}>
        {arActive && (
          <div className="fixed inset-0 z-[60] flex flex-col justify-between" data-testid="ar-overlay">
            <div className="flex flex-col items-center gap-2 p-4">
              {!arReady && (
                <div data-testid="ar-guide-message" className="max-w-sm animate-pulse bg-black/70 px-4 py-2 text-center text-[13px] font-semibold leading-snug text-white">
                  {everHitRef.current ? t("arLost") : t("arScanning")}
                </div>
              )}
              <div className="flex flex-col items-center gap-1 bg-black/70 px-4 py-2">
                {rooms[arRoomIdx.current]?.name && (
                  <span data-testid="ar-room-name" className="text-xs font-bold text-white">{rooms[arRoomIdx.current].name}</span>
                )}
                <div className="flex items-center gap-3">
                  <span className="text-[11px] uppercase tracking-wider text-white/60">{t("totalArea")}</span>
                  <span data-testid="ar-live-area" className="font-mono text-xl font-black" style={{ color: BRAND_CSS }}>
                    {liveArea.toFixed(2)} m²
                  </span>
                </div>
              </div>
            </div>

            <div className="flex w-full items-stretch gap-2 p-3 pb-6">
              <Button data-testid="ar-cancel" onClick={arCancel} className="flex h-12 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-none border border-white/60 bg-red-600 p-0 text-white hover:bg-red-700">
                <X className="h-5 w-5" />
                <span className="text-[9px] font-bold leading-none">{t("exitAr")}</span>
              </Button>
              <Button data-testid="ar-undo" onClick={arUndo} disabled={arCount === 0} className="flex h-12 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-none border border-white/60 bg-neutral-800 p-0 text-white hover:bg-neutral-700 disabled:opacity-40">
                <Undo2 className="h-5 w-5" />
                <span className="text-[9px] font-bold leading-none">{t("undoAr")}</span>
              </Button>
              <Button data-testid="ar-add" onClick={arAddPoint} className="h-12 flex-1 rounded-none bg-primary px-2 text-sm font-bold text-primary-foreground">
                <Plus className="mr-1 h-4 w-4" /> {t("arAddPoint")} · {arCount}
              </Button>
              <Button data-testid="ar-finish" onClick={arFinish} disabled={arCount < 3} className="h-12 w-14 shrink-0 rounded-none bg-success p-0 font-bold text-white">
                <Check className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} modal={!arActive} onOpenChange={(o) => { if (!o && !arActive) onClose(); }}>
        <DialogContent
          data-testid="area-calculator"
          className="rounded-none border-border sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Ruler className="h-5 w-5 text-primary" /> {t("areaCalcTitle")}
            </DialogTitle>
          </DialogHeader>

          {arSupported && (
            <div className="flex items-center gap-2 border border-primary/40 bg-primary/5 px-3 py-2 text-[11px] text-muted-foreground">
              <Camera className="h-3.5 w-3.5 shrink-0 text-primary" /> {t("arScanHint")}
            </div>
          )}

          <p className="text-xs text-muted-foreground">{t("areaCalcHint")}</p>

          <div className="max-h-[52vh] space-y-3 overflow-y-auto pr-1">
            {rooms.map((room, ri) => (
              <div key={ri} className="border border-border bg-surface p-3" data-testid={`room-${ri}`}>
                <div className="mb-2 flex items-center gap-2">
                  <Input
                    data-testid={`room-name-${ri}`}
                    value={room.name}
                    onChange={(e) => renameRoom(ri, e.target.value)}
                    placeholder={t("roomNamePlaceholder")}
                    className="h-8 flex-1 rounded-none border-border text-sm font-semibold"
                  />
                  <span data-testid={`room-total-${ri}`} className="shrink-0 font-mono text-sm font-bold text-primary">{roomArea(room).toFixed(2)} m²</span>
                  {rooms.length > 1 && (
                    <button data-testid={`room-remove-${ri}`} onClick={() => removeRoom(ri)} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {room.ar != null ? (
                  <div className="flex items-center justify-between border border-primary/40 bg-primary/10 px-3 py-2">
                    <span className="flex items-center gap-2 text-xs font-semibold text-foreground">
                      <Camera className="h-3.5 w-3.5 text-primary" /> {t("arMeasured")}: {room.ar.toFixed(2)} m²
                    </span>
                    <button data-testid={`room-clear-ar-${ri}`} onClick={() => clearRoomAr(ri)} className="text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {room.sections.map((s, si) => (
                      <div key={si} className="flex items-center gap-2" data-testid={`room-${ri}-section-${si}`}>
                        <Input data-testid={`area-w-${ri}-${si}`} type="number" inputMode="decimal" placeholder={t("width")} value={s.w}
                          onChange={(e) => updateSection(ri, si, "w", e.target.value)} className="h-9 rounded-none border-border font-mono" />
                        <span className="text-muted-foreground">×</span>
                        <Input data-testid={`area-l-${ri}-${si}`} type="number" inputMode="decimal" placeholder={t("length")} value={s.l}
                          onChange={(e) => updateSection(ri, si, "l", e.target.value)} className="h-9 rounded-none border-border font-mono" />
                        <span className="w-16 shrink-0 text-right font-mono text-xs">{(num(s.w) * num(s.l)).toFixed(1)} m²</span>
                        <button data-testid={`area-remove-${ri}-${si}`} onClick={() => removeSection(ri, si)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-2">
                      <button data-testid={`area-add-section-${ri}`} onClick={() => addSection(ri)} className="flex items-center gap-1 border border-dashed border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-background">
                        <Plus className="h-3.5 w-3.5" /> {t("addSection")}
                      </button>
                      {arSupported && (
                        <button data-testid={`room-ar-${ri}`} onClick={() => startRoomAR(ri)} className="flex items-center gap-1 border border-primary/60 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold hover:bg-primary/20">
                          <Camera className="h-3.5 w-3.5 text-primary" /> {t("measureRoomAR")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button data-testid="area-add-room" variant="outline" onClick={addRoom} className="w-full rounded-none border-dashed border-border">
            <Plus className="mr-2 h-4 w-4" /> {t("addRoom")}
          </Button>

          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="label-caps text-muted-foreground">{t("totalArea")}</span>
            <span data-testid="area-total" className="font-mono text-2xl font-black text-foreground">{total.toFixed(2)} m²</span>
          </div>

          <Button data-testid="area-confirm" onClick={confirm} disabled={total <= 0} className="w-full rounded-none bg-primary text-primary-foreground hover:opacity-90">
            <Check className="mr-2 h-4 w-4" /> {t("useArea")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Small inline button to sit next to an m² input, opens the calculator.
export const SmartAreaButton = ({ onResult }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="smart-area-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 whitespace-nowrap border border-primary/60 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-primary/20"
      >
        <Camera className="h-3.5 w-3.5 text-primary" /> {t("smartArea")}
      </button>
      <AreaCalculator open={open} onClose={() => setOpen(false)} onConfirm={onResult} />
    </>
  );
};
